import React from 'react';
import { Link } from 'react-router-dom';
import LegalFooter from '../components/LegalFooter';

export default function Enforcement() {
  return (
    <div className="min-h-screen bg-white" data-testid="enforcement-page">
      <header className="border-b border-zinc-200">
        <div className="container mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="header-logo text-xl">AI Accountability Standards</Link>
            <nav className="flex items-center gap-6">
              <Link to="/registry" className="nav-link">Registry</Link>
              <Link to="/tools" className="nav-link">Tools</Link>
            </nav>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-8 py-12">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl font-semibold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Enforcement and Withdrawal
          </h1>
          <p className="text-sm text-zinc-500 mb-8">AI Accountability Standards Registry</p>
          
          <div className="prose prose-zinc max-w-none space-y-6 text-zinc-700">
            
            <p className="font-medium bg-zinc-100 p-4 border-l-4 border-zinc-400">
              Enforcement protects trust, not individual firms.
            </p>

            <section>
              <h2 className="text-xl font-semibold text-zinc-900 mt-8 mb-4">1. Enforcement Authority</h2>
              <p>
                The AI Accountability Standards Council maintains sole authority over enforcement actions related to declarations issued through the Registry. Enforcement decisions are made at the Council's discretion to protect the integrity of the declaration program and public trust.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-zinc-900 mt-8 mb-4">2. Denial of Declaration</h2>
              <p>
                Declaration may be denied for reasons including but not limited to:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Failure to meet eligibility requirements</li>
                <li>Incomplete or inaccurate application information</li>
                <li>Failure to complete required assessments</li>
                <li>Insufficient demonstration of standards knowledge</li>
                <li>Misrepresentation of organizational status or capabilities</li>
                <li>Prior revocation for cause</li>
                <li>Other reasons at the Council's sole discretion</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-zinc-900 mt-8 mb-4">3. Withdrawal of Declaration</h2>
              <p>
                Issued declarations may be withdrawn for reasons including but not limited to:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Material misrepresentation during the declaration process</li>
                <li>Discovery of inaccurate or false information in application</li>
                <li>Non-compliance with declared standards</li>
                <li>Conduct that brings the declaration program into disrepute</li>
                <li>Misuse or misrepresentation of declaration status</li>
                <li>Failure to maintain eligibility requirements</li>
                <li>Non-payment of applicable fees</li>
                <li>Other misconduct at the Council's sole discretion</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-zinc-900 mt-8 mb-4">4. Permanent Record</h2>
              <p>
                The public Registry maintains a permanent record of all declaration actions:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Withdrawn declarations remain visible in the Registry</li>
                <li>Expired declarations remain visible with expiry date</li>
                <li>Withdrawal date and reason are publicly displayed</li>
                <li>Historical status is preserved for verification purposes</li>
              </ul>
              <p className="mt-3 font-medium">
                Declaration records are not removed upon withdrawal or expiry. The Registry reflects status permanently.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-zinc-900 mt-8 mb-4">5. No Obligation to Appeal</h2>
              <p>
                The Council is under no obligation to provide an appeal process for enforcement decisions. Declaration decisions, including denials and revocations, are final.
              </p>
              <p className="mt-3">
                The Council is not obligated to:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Provide detailed rationale for enforcement decisions</li>
                <li>Disclose internal deliberations or review processes</li>
                <li>Offer formal appeal procedures</li>
                <li>Reconsider finalized enforcement actions</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-zinc-900 mt-8 mb-4">6. Refunds</h2>
              <p>
                Refund eligibility following enforcement actions is determined at the Council's discretion:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Denial due to applicant misrepresentation: No refund</li>
                <li>Withdrawal for cause: No refund</li>
                <li>Denial for other reasons: Refund may be issued at Council discretion</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-zinc-900 mt-8 mb-4">7. Reporting Concerns</h2>
              <p>
                Third parties may report concerns regarding declared organizations to enforcement@aiaccountabilitycouncil.com. Reports are reviewed at the Council's discretion. The Council is not obligated to:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li>Acknowledge or respond to all reports</li>
                <li>Disclose investigation status or outcomes</li>
                <li>Take action on every report received</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-zinc-900 mt-8 mb-4">8. Authority Reference</h2>
              <p>
                Enforcement policies are established by the AI Accountability Standards Council. Complete policy documentation is available at <a href="https://aiaccountabilitycouncil.com" target="_blank" rel="noopener noreferrer" className="text-zinc-900 underline">aiaccountabilitycouncil.com</a>.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-zinc-900 mt-8 mb-4">Contact</h2>
              <p>
                Questions regarding enforcement policies may be directed to{' '}
                <a href="mailto:contact@aiaccountabilitycouncil.com" className="text-zinc-900 underline">contact@aiaccountabilitycouncil.com</a>
                {' '}or through the{' '}
                <Link to="/contact" className="text-zinc-900 underline">contact form</Link>.
              </p>
            </section>
          </div>
        </div>
      </main>

      <LegalFooter />
    </div>
  );
}
